import { useQuery } from '@apollo/client/react'
import React, { useState } from 'react'
import styled from 'styled-components'
import { Button } from '../../../components/Button'
import { ButtonColumn, ButtonRow, Column, Page, Row, FlexContainer, Heading3 } from '../../../components/layout'
import {
  GenericResultHandler,
  GenericResultHandlerDefinition,
} from '../../../components/responses/GenericResultHandler'
import { useHistoryPush } from '../../../components/router'
import { TowingRecordRow } from '../../../components/TowingRecordRow'
import { getStationByIdFromSettings } from '../../Parameters/station'
import { getOperatorLabelFromId } from '../../Parameters/user'
import { messages } from '../messages'

import { TOWING_JOBS_SEARCH_QUERY, TowingJobsResponse } from '../TowingJobsQuery'
import { initialValues, TowingJobFilter } from '../../../components/TowingJobFilter/TowingJobFilter'
import { ActionButtons, FilterModal, Search } from '../../../components/TowingJobFilter/components/FilterModal'
import Link from '../../../components/Link'
import { useRepairShops } from '../../TowingRecordPage/components/routes/subforms/FinishRoute/options'
import { formatMessageSafe } from '../../../util/translateInputOptions'
import { useIntl } from 'react-intl'
import { UserRole } from '../../../common/types'
import { useSettings } from '../../Parameters'

const HeaderContainer = styled(FlexContainer as any)`
  @media (max-width: ${props => props.theme.screenSize.mobile}) {
    justify-content: space-between;
  }
`

const AddButton = styled(Button as any)`
  max-width: 100%;
`

const CompletedJobsLink = styled(Link as any)`
  margin-left: 2rem;
`

const CompletedTowingJobsPage: React.FunctionComponent = () => {
  const settings = useSettings()
  const [expandedRowId, clickOnRow] = useState<number | null>(null)
  const handleItemClick = (clickedId: number) => {
    clickOnRow(clickedId !== expandedRowId ? clickedId : null)
  }

  const TypedGenericResultHandler = GenericResultHandler as GenericResultHandlerDefinition<TowingJobsResponse>
  const historyPush = useHistoryPush()
  const intl = useIntl()
  const formatMessage = formatMessageSafe(intl)
  const translateMessage = (messageId: string) => formatMessage({ id: messageId })

  // Dummy user for the hidden filter component
  const dummyUser = {
    mfa_status: '',
    user_id: 0,
    rememberMeForDays: '',
    company_id: 0,
    role: UserRole.user,
    exp: '',
    username: '',
    realName: '',
  }
  const towingJobsQueryResult = useQuery<TowingJobsResponse>(TOWING_JOBS_SEARCH_QUERY, {
    variables: {
      filters: {
        ...initialValues.filters,
        includeTowingOrders: false,
      },
      status: 'COMPLETED',
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  })
  const { repairShops } = useRepairShops({ skip: !settings || !settings.modules.autoliitto })

  return (
    <TypedGenericResultHandler queryResult={towingJobsQueryResult} loading unauthorized>
      {responseData => {
        const data = responseData
        return (
          <Page noPadding>
            <Row space="double" paddingMobile={"0.5em 1.5em 0em 1.5em"}>
              <Column>
                <HeaderContainer>
                  <Heading3>Valmiit työt</Heading3>
                  <CompletedJobsLink to="/towing-jobs">Työlista</CompletedJobsLink>
                </HeaderContainer>
              </Column>
              <Column>
                <ButtonRow padding="0">
                  <ButtonColumn>
                    <AddButton
                      category="new"
                      label={formatMessage(messages.newTowingOrderButton)}
                      onClick={() => {
                        historyPush('/towing-order/add')
                      }}
                    />
                  </ButtonColumn>
                </ButtonRow>
              </Column>
            </Row>
            <Row space="double">
              <Column>
                <HeaderContainer justifyContent="space-between">
                  <TowingJobFilter
                    show={false}
                    currentUser={dummyUser}
                    loading={towingJobsQueryResult.loading}
                    setValues={() => {}}
                    resetToDefaultOptions={() => {}}
                    values={{
                      ...initialValues,
                      filters: {
                        ...initialValues.filters,
                      },
                      status: 'COMPLETED',
                    }}
                  >
                    <FilterModal>
                      <Search />
                      <ActionButtons />
                    </FilterModal>
                  </TowingJobFilter>
                </HeaderContainer>
              </Column>
            </Row>
            {data &&
              data.towingRecords.towingRecords.map(towingRecord => {
                return (
                  <TowingRecordRow
                    repairShops={repairShops}
                    expandedRow={expandedRowId === towingRecord.id}
                    handleItemClick={handleItemClick}
                    towingRecord={towingRecord}
                    translateMessage={translateMessage}
                    key={towingRecord.id}
                    slug={`/towing-record/${towingRecord.id}`}
                    station={getStationByIdFromSettings(towingRecord.stationId, settings)}
                    operatorName={getOperatorLabelFromId(
                      towingRecord.operatorId,
                      null,
                      settings ? settings.operators : null
                    )}
                  />
                )
              })}
          </Page>
        )
      }}
    </TypedGenericResultHandler>
  )
}

export default CompletedTowingJobsPage
